import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { subscribeAuthChange } from './AuthContext';

const CartContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getOrCreateGuestId = () => {
  let id = sessionStorage.getItem('guestId');
  if (!id) {
    id = 'guest-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
    sessionStorage.setItem('guestId', id);
  }
  return id;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(() => getOrCreateGuestId());
  const lastMergedRef = useRef(null);

  // Sync userId with the authenticated user (poll lightly via /api/auth/me)
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      const guestId = sessionStorage.getItem('guestId') || getOrCreateGuestId();
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        const realId = data.id;
        if (cancelled) return;
        // Merge guest cart into user cart only once per user.id
        if (realId && lastMergedRef.current !== realId) {
          lastMergedRef.current = realId;
          try {
            const merged = await axios.post(
              `${API}/cart/merge?guest_id=${encodeURIComponent(guestId)}&user_id=${encodeURIComponent(realId)}`
            );
            setCart(merged.data);
          } catch {
            /* fallback: just load */
          }
        }
        setUserId(realId);
      } catch {
        // not logged in -> use guest id
        if (!cancelled) setUserId(guestId);
      }
    };
    sync();
    // listen for storage / focus to re-sync after login in another tab
    const onStorage = () => sync();
    window.addEventListener('focus', onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onStorage);
    };
  }, []);

  // Load cart whenever userId changes
  useEffect(() => {
    if (!userId) return;
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadCart = async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/cart/${userId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!userId) return { success: false };
    setLoading(true);
    try {
      const response = await axios.post(`${API}/cart`, { userId, productId, quantity });
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!userId) return { success: false };
    setLoading(true);
    try {
      const response = await axios.put(`${API}/cart/${userId}/${productId}?quantity=${quantity}`);
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error updating quantity:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    if (!userId) return { success: false };
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/cart/${userId}/${productId}`);
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error removing from cart:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    if (!userId) return { success: false };
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/cart/clear/${userId}`);
      setCart(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const onAuthChanged = async ({ user }) => {
    if (user) {
      const guestId = sessionStorage.getItem('guestId') || getOrCreateGuestId();
      try {
        const { data } = await axios.post(
          `${API}/cart/merge?guest_id=${encodeURIComponent(guestId)}&user_id=${encodeURIComponent(user.id)}`
        );
        setCart(data);
      } catch {
        /* ignore */
      }
      lastMergedRef.current = user.id;
      setUserId(user.id);
    } else {
      // logout: get a fresh guest id
      const newGuest = 'guest-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
      sessionStorage.setItem('guestId', newGuest);
      lastMergedRef.current = null;
      setUserId(newGuest);
    }
  };

  // Subscribe to AuthContext changes
  useEffect(() => {
    return subscribeAuthChange(onAuthChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCartCount = () => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        userId,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getCartCount,
        loadCart,
        onAuthChanged,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
